import { Alert, Box, Button, FormControl, FormLabel, IconButton, Input, Modal, ModalClose, ModalDialog, ModalOverflow, Option, Select, Sheet, Stack, Textarea, Typography } from "@mui/joy";
import { useContext, useEffect, useState } from "react";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';

import { motion, AnimatePresence } from "framer-motion";
import { LayoutContext } from "../context";
import { APICreateSuggestion } from "../api";


// Категории предложений (соответствуют модели Suggestion)
const SUGGESTION_CATEGORIES = [
    { value: 'feature', label: 'Новая функция' },
    { value: 'improvement', label: 'Улучшение' },
    { value: 'bugfix', label: 'Исправление ошибки' },
    { value: 'design', label: 'Дизайн' },
    { value: 'other', label: 'Другое' }
];

// Приоритеты предложений
const PRIORITIES = [
    { value: 1, label: 'Низкий' },
    { value: 2, label: 'Средний' },
    { value: 3, label: 'Высокий' },
    { value: 4, label: 'Критический' }
];


export default function SuggestionModal() {
    const { deviceType, modalSuggestion, setModalSuggestion, token } = useContext(LayoutContext);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'improvement',
        priority: 2
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    useEffect(() => {
        if (modalSuggestion.open) {
            // Сброс формы при открытии
            setFormData({
                title: '',
                description: '',
                category: 'improvement',
                priority: 2
            });
            setSubmitStatus(null);
        }
    }, [modalSuggestion.open]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.description) {
            setSubmitStatus('error');
            return;
        }

        setIsSubmitting(true);
        try {
            await APICreateSuggestion(token, formData);
            
            setSubmitStatus('success');
            // Закрываем модальное окно через 2 секунды после успешной отправки
            setTimeout(() => {
                setModalSuggestion({ open: false });
            }, 2000);
        } catch (error) {
            console.error('Ошибка при отправке предложения:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setModalSuggestion({ open: false });
    };

    return (
        <AnimatePresence>
            {modalSuggestion?.open && (
                <Modal
                    sx={{ zIndex: 10002 }}
                    open={modalSuggestion?.open}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <Box sx={{ mt: deviceType == 'Phone' ? 10 : 0 }}></Box>

                            <ModalClose sx={{ mt: deviceType == 'Phone' ? 10 : 0 }} onClick={handleClose}/>
                            
                            <Typography level="h3" component="h2" sx={{ mb: 2 }}>
                                Предложение по улучшению
                            </Typography>

                            {submitStatus === 'success' ? (
                                <Alert color="success" sx={{ mb: 2 }}>
                                    <Typography level="body-sm">
                                        Спасибо за ваше предложение! Мы рассмотрим его в ближайшее время.
                                    </Typography>
                                </Alert>
                            ) : (
                                <>
                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Заголовок предложения *</FormLabel>
                                        <Input
                                            value={formData.title}
                                            onChange={(e) => handleInputChange('title', e.target.value)}
                                            placeholder="Краткое описание вашего предложения"
                                        />
                                    </FormControl>

                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Категория</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                            value={formData.category}
                                            onChange={(_, value) => handleInputChange('category', value)}
                                        >
                                            {SUGGESTION_CATEGORIES.map(category => (
                                                <Option key={category.value} value={category.value}>
                                                    {category.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Приоритет</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                            value={formData.priority}
                                            onChange={(_, value) => handleInputChange('priority', value)}
                                        >
                                            {PRIORITIES.map(priority => (
                                                <Option key={priority.value} value={priority.value}>
                                                    {priority.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ mb: 3 }}>
                                        <FormLabel>Подробное описание *</FormLabel>
                                        <Textarea
                                            minRows={4}
                                            placeholder="Опишите ваше предложение подробно..."
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                        />
                                    </FormControl>

                                    {submitStatus === 'error' && (
                                        <Alert color="danger" sx={{ mb: 2 }}>
                                            <Typography level="body-sm">
                                                Пожалуйста, заполните обязательные поля и попробуйте снова.
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={handleClose}>
                                            Отмена
                                        </Button>
                                        <Button 
                                            loading={isSubmitting}
                                            onClick={handleSubmit}
                                            disabled={!formData.title || !formData.description}
                                        >
                                            Отправить предложение
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}